import {filter, forEach, isEmpty, map} from 'lodash';
import moment from 'moment';
import {Suspense, lazy, useCallback, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Loader} from 'components';
import {themeConfig} from 'constants/theme';
import {UserContext} from 'context';
import {TRANSLATION_KEYS} from 'locale';
import {WorkingHoursInterval} from 'types';
import {lazyModules} from 'utils/lazy-modules';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.WORKING_HOURS.TIMELINE.USERS_CHART;

type Props = {
    usersIntervalsData: {userName: string, intervals: WorkingHoursInterval[]}[];
};

const Chart = lazy(() => lazyModules.reactGoogleCharts());

export const UsersTimeline: React.FC<Props> = ({usersIntervalsData = []}) => {
    const {t} = useTranslation();
    const {settings: {common: {timeFormat}}} = useContext(UserContext);

    const columns = useMemo(() => [
        {type: 'string', id: 'Role'},
        {type: 'string', id: 'Name'},
        {type: 'string', id: 'style', role: 'style'},
        {type: 'string', role: 'tooltip'},
        {type: 'date', id: 'Start'},
        {type: 'date', id: 'End'}
    ], []);

    const tableHeight = useMemo(() => {
        let height = 90;
        if (usersIntervalsData.length < 4) {
            height += usersIntervalsData.length * 45;
        } else {
            height = 400;
        }
        return `${height}px`;
    }, [usersIntervalsData]);

    const getDurationLabel = useCallback((durationMinutes: number) => {
        let hoursLabel = '';
        let minutesLabel = '';
        const value = Math.round(durationMinutes);
        if (value >= 60) {
            hoursLabel = `${Math.floor(value / 60)} ${t(T_KEYS.HOURS)}`;
        }
        const minutes = value % 60;
        if (minutes) {
            minutesLabel = `${minutes} ${t(T_KEYS.MINUTES)}`;
        }

        return filter([hoursLabel, minutesLabel]).join(' ');
    }, [t]);

    const buildTooltipHtml = useCallback((label: string, start: moment.Moment, _end: moment.Moment) => {
        const end = _end.minute(Math.round(_end.minute() / 15) * 15); // round time
        const diff = end.diff(start);
        const durationMinutes = moment.duration(diff).asMinutes();
        if (durationMinutes < 1) {
            return '';
        }

        return `
            <div>
                <div style='
                    border-bottom: 1px solid LightGray;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    white-space: nowrap;
                    font-size: 14px;
                    font-weight: bold;
                    padding: 6px;
                '>${label}</div>
                <div style='padding: 6px'>
                    ${start.format(timeFormat)} - ${end.format(timeFormat)}
                    <div style='margin-top: 4px;'><span style='font-weight: bold;'>
                        ${t(T_KEYS.DURATION)}: </span>${getDurationLabel(durationMinutes)}
                    </div>
                </div>
            </div>
        `;
    }, [timeFormat, getDurationLabel, t]);

    const buildIntervalsRows = useCallback((label: string, intervals: WorkingHoursInterval[], barText = '', style = '') =>
        map(intervals, (interval) => {
            const startMoment = moment().set('h', interval.from.hours).set('m', interval.from.minutes);
            const endMoment = moment().set('h', interval.to.hours).set('m', interval.to.minutes);
            return [
                label,
                barText,
                style,
                buildTooltipHtml(label, startMoment, endMoment),
                startMoment.toDate(),
                endMoment.toDate()
            ];
        }), [buildTooltipHtml]);

    const rows = useMemo(() => {
        const result = [];
        forEach(usersIntervalsData, (intervalData) => {
            if (isEmpty(intervalData.intervals)) {
                const emptyIntervals = [{from: {hours: 0, minutes: 0}, to: {hours: 0, minutes: 0}}];
                result.push(...buildIntervalsRows(intervalData.userName, emptyIntervals, t(T_KEYS.NON_WORKING_DAY), 'opacity: 0;'));
            }
            result.push(...buildIntervalsRows(intervalData.userName, intervalData.intervals));
        });

        return [columns, ...result];
    }, [usersIntervalsData, buildIntervalsRows, columns, t]);

    const options = useMemo(() => {
        const now = moment();
        return {
            timeline: {
                singleColor: themeConfig.colors.accentSky
            },
            alternatingRowStyle: false,
            hAxis: {
                minValue: now.startOf('d'),
                maxValue: now.clone().add(1, 'd'),
                format: timeFormat
            }
        };
    }, [timeFormat]);

    return (
        <div className='tw-w-full tw-overflow-x-auto'>
            <div className='tw-relative tw-mt-4 tw-min-w-256 tw-overflow-y-hidden '>
                <Suspense fallback={<Loader />}>
                    <Chart
                        chartType='Timeline'
                        data={rows}
                        width='100%'
                        height={tableHeight}
                        options={options}
                    />
                </Suspense>
            </div>
        </div>
    );
};